/**
 * This code defines a function getDistinct that takes an array of objects and an array of attribute names.
 *  It returns an object that represents the count of distinct string values extracted from the objects based on the specified attributes.
 *  The function iterates over each object and attribute combination, and recursively extracts the values from nested attributes.
 *  The returned object contains each distinct string value as a key and its count as the value.
 *
 * @param {T[]} arr - The array of objects.
 * @param {string[]} attribute - The attribute to extract values from.
 * @return {string[]} - An array of distinct string values.
 */
export function getDistinct<T>(arr: any[], attributes: string[], valueCount?: string): Array<{ [attribute: string]: any }> {
  const counts: { [value: string]: number } = {};

  function extractValue(item: any, nestedAttributes: string[]): void {
    if (!item) {
      const count = (counts[String('NotFound')] || 0) + 1;
      counts[String('NotFound')] = count;
      return;
    }
    const attribute = nestedAttributes.shift();
    if (attribute !== undefined) {
      const value = item[attribute];
      if (value !== undefined) {
        if (nestedAttributes.length === 0) {
          const count = (counts[String(value)] || 0) + 1;
          counts[String(value)] = count;
        } else if (typeof value === 'object') {
          extractValue(value, nestedAttributes);
        }
      }
    }
  }

  if (Array.isArray(arr)) {
    arr.forEach(function (item) {
      attributes.forEach(function (attribute) {
        extractValue(item, attribute.split('.'));
      });
    });
  }

  const result: Array<{ [attribute: string]: any }> = [];
  Object.entries(counts).forEach(([value, count]) => {
    const obj: { [attribute: string]: any } = {};
    let item = arr.find(item => item.logicalId === value);
    if (!item) item = arr.find(item => item.eventTypeId === value)?.eventType;
    if (!item) item = arr.find(item => item.readingTypeId === value)?.readingType;

    attributes.forEach(attribute => {
      obj['name'] = (item && item.name) || undefined;
      obj[attribute] = value;
      obj['count'] = valueCount ? item[valueCount] : count;
    });
    result.push(obj);
  });

  return result.sort((a, b) => b['count'] - a['count']);
}

export function sumCountsByFlavor(arr: any[]) {
  const counts: { [value: string]: number } = {};
  for (let obj of arr) {
    const flavor = obj.flavor;
    if (flavor && obj.count) {
      counts[flavor] = (counts[flavor] || 0) + obj.count;
    }
  }
  const obj: { [attribute: string]: any } = {};
  for (let flavor in counts) {
    obj[flavor] = counts[flavor];
  }
  return obj;
}

export function sortByAttribute(arr: any[], attribute: string, ascending?: boolean) {
  return arr.sort((a, b) => {
    if (ascending) {
      if (typeof a[attribute] === 'string' && typeof b[attribute] === 'string') {
        return a[attribute].localeCompare(b[attribute]);
      } else {
        return a[attribute] - b[attribute];
      }
    } else {
      if (typeof a[attribute] === 'string' && typeof b[attribute] === 'string') {
        return b[attribute].localeCompare(a[attribute]);
      } else {
        return b[attribute] - a[attribute];
      }
    }
  });
}
/**
 * Generates an array of dates based on the given current date and type.
 *
 * @param {Date} currentDate - The current date.
 * @param {string} type - The type of date array to generate. Can be 'week' or 'month'.
 * @return {Array} An array of dates based on the current date and type.
 */
export function generateDateArray(currentDate: Date, type: string) {
  const dateArray: any[] = [];

  if (type === 'week') {
    const startDate = new Date(currentDate);
    startDate.setDate(startDate.getDate() - 6);
    startDate.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dateArray.push(date);
    }
  } else if (type === 'month') {
    const startMonth = new Date(currentDate);
    startMonth.setDate(1);
    startMonth.setHours(0, 0, 0, 0);

    for (let i = 0; i < 12; i++) {
      const date = new Date(startMonth);
      date.setMonth(startMonth.getMonth() - i);
      dateArray.push(date);
    }
    dateArray.reverse();
  }

  return dateArray;
}
